import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { NDfModuleLoqate } from '@df/module-loqate/df-module-loqate.config';
import { LoqatePhoneValidateDirective } from '@df/module-loqate/phone/loqate-phone.validator';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import allCountries from '@df/ui/form/phone/phone-module/countries-list';
import { ToggleModule } from '@df/ui/toggle/toggle.module';
import { NToggle } from '@df/ui/toggle/toggle.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { Nexus } from '@tomandco/nexus-api-types';
import sortBy from 'lodash-es/sortBy';
import { CheckoutFormComponent } from '../../../checkout/checkout-form.component';
import { CountryFlagComponent } from '../../display/country-flag/country-flag.component';
import { InputWrapBodyDirective } from '../input-wrap-body.directive';
import { InputWrapComponent } from '../input-wrap.component';
import { LocalAddressFieldsetComponentDef } from '../local-address-fieldset-def.component';
import { MuiInputDirective } from '../mui-input.directive';
import { ToggleComponent } from '../toggle/toggle.component';
import { ValidatePatternAsyncDirective } from '../validate/validate-pattern-acync.directive';
import { pushCountriesToFront, PushCountriesToFrontPipe } from './push-countries-to-front.pipe';
import { SearchCountriesPipe } from './search-countries.pipe';
import { injectTimeoutService } from '@df/ng/timeout.service';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface Country {
            dialCode: string | number;
            availableNumbers: string[];
        }
    }
}

@Component({
    selector: 'address-fieldset',
    templateUrl: './address-fieldset.component.html',
    styleUrl: './address-fieldset.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        CountryFlagComponent,
        FormsModule,
        InputWrapBodyDirective,
        InputWrapComponent,
        LoqatePhoneValidateDirective,
        MuiInputDirective,
        OffClickDirective,
        OrderByPipe,
        PushCountriesToFrontPipe,
        SearchCountriesPipe,
        ToggleComponent,
        ToggleModule,
        TranslatePipe,
        ValidatePatternAsyncDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }]
})
export class AddressFieldsetComponent extends LocalAddressFieldsetComponentDef implements OnInit {
    @Input()
    expandable = false;

    @Input()
    phoneFlags = false;

    @ViewChild('telephoneRef', { read: ElementRef })
    telephoneRef!: ElementRef<HTMLInputElement>;

    @ViewChild('countrySearchRef', { read: ElementRef })
    countrySearchRef!: ElementRef<HTMLInputElement>;

    @ViewChild('phoneSearchRef', { read: ElementRef })
    phoneSearchRef!: ElementRef<HTMLInputElement>;

    countryQuery?: string;

    get countryCodesDefault(): string[] {
        const currentCountry = this.app.multisite.current.country || 'GB';

        const countries = {
            GB: ['GB', 'IE', 'US'],
            US: ['US', 'GB', 'IE'],
            IE: ['IE', 'GB', 'US']
        };

        return countries[currentCountry] || countries.GB;
    }

    countryCodes = [...this.countryCodesDefault];

    phoneQuery?: string;
    phoneTmp?: string;
    phoneIsValid = false;
    phoneBlur = false;

    private _phoneCountryCode?: string;
    // last manually selected country code
    private _previousPhoneCountryCode?: string;
    private _countries: Nexus.Lib.General.Country[] = [];

    readonly countryToggleId: NToggle.TId = ['country', 0];
    readonly phoneToggleId: NToggle.TId = ['phone', 0];

    protected checkoutFormComponent = inject(CheckoutFormComponent, { optional: true });
    protected readonly disposableTimeoutService = injectTimeoutService();

    set phoneCountryCode(value: string | undefined) {
        this._phoneCountryCode = value;
    }

    get phoneCountryCode(): string | undefined {
        return this._phoneCountryCode || this.countryCode;
    }

    /**
     * Countries list
     * @type {array}
     */
    override get countries(): Nexus.Lib.General.Country[] {
        if (!this._countries || !this._countries.length) {
            const countries = super.countries;

            this._countries = countries.map(country => {
                const data = allCountries.find(c => c.code.toLowerCase() === country.code.toLowerCase());

                if (!data) {
                    return country;
                }

                const dialCode = data?.dialCode;
                const areaCodes: string[] = typeof data?.areaCodes === 'string' ? [data?.areaCodes] : data?.areaCodes || [];

                const availableNumbers = [`+${dialCode}`];

                areaCodes.forEach(areaCode => {
                    availableNumbers.push(`+${dialCode}${areaCode}`);
                    availableNumbers.push(`+${dialCode}(${areaCode})`);
                });

                return {
                    ...country,
                    dialCode: `+${dialCode}${areaCodes.length ? '(' + (areaCodes.length === 1 ? areaCodes[0] : '###') + ')' : ''}`,
                    availableNumbers
                };
            });
        }

        return this._countries;
    }

    get countrySelectState() {
        return this.toggleService.getState(this.countryToggleId);
    }

    get phoneSelectState() {
        return this.toggleService.getState(this.phoneToggleId);
    }

    get phoneValidationEnabled(): boolean {
        return (this.telephoneRef?.nativeElement?.value?.length > 0 && this.phoneBlur) || this.form.submitAttempt;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.subscriptions.add(
            this.toggleService.getState(this.countryToggleId).subscribe(state => {
                if (state) {
                    this.disposableTimeoutService.setTimeout(() => this.countrySearchRef?.nativeElement?.focus(), 100);
                }
            })
        );

        this.subscriptions.add(
            this.toggleService.getState(this.phoneToggleId).subscribe(state => {
                if (state) {
                    this.disposableTimeoutService.setTimeout(() => this.phoneSearchRef?.nativeElement?.focus(), 100);
                }
            })
        );

        this.app.apiConfigService.loadCountries().then(() => {
            this.syncCountries();

            this.detectChanges();
        });

        if (this.checkoutFormComponent) {
            if (this.isBilling) {
                this.phoneTmp = this.checkoutFormComponent!.billingAddress!.telephone;
            } else {
                this.phoneTmp = this.checkoutFormComponent!.shippingAddress!.telephone;
            }
        } else {
            this.phoneTmp = this.data.telephone;
        }

        this.onInputKeyUp();
    }

    onInputKeyUp(): void {
        this.phoneBlur = false;
        this.phoneIsValid = false;
        const telephone = this.phoneTmp;

        this.changeDetectorRef.detectChanges();

        if (telephone) {
            if (telephone === '+') {
                this.phoneCountryCode = 'xx';
            } else if (telephone.length > 1) {
                const country = pushCountriesToFront(
                    sortBy(this.countries as Nexus.Lib.General.Country[], ['priority', 'name']),
                    this.countryCodes
                ).find(country => !!country.availableNumbers?.find(number => telephone.startsWith(number)));

                if (country && (this.phoneCountryCode === 'xx' || country.code !== this._previousPhoneCountryCode)) {
                    this.app.logger.info('Country found %s (%s), available codes:', country.name, country.code, country.availableNumbers);
                    this.selectPhoneCode(country.code, false);
                }
            } else {
                this.phoneCountryCode = this._previousPhoneCountryCode || this.countryCode;
            }
        }
    }

    onPhoneInputBlur(): void {
        this.onInputBlur();

        this.phoneBlur = true;
        this.changeDetectorRef.detectChanges();
    }

    selectCountry(id: string): void {
        this.data.country_id = id;

        this.closeCountrySelect();
    }

    toggleCountrySelect(): void {
        this.toggleService.toggle(this.countryToggleId);
    }

    closeCountrySelect(): void {
        this.toggleService.close(this.countryToggleId);

        this.countryQuery = '';
    }

    /**
     * Triggered when phone country code changes
     */
    onPhoneResponse(data: NDfModuleLoqate.PhoneValidationItem[] | undefined): void {
        if (data) {
            const item = data[0];

            if ((item?.IsValid === 'Yes' || item?.IsValid === 'Maybe') && item?.PhoneNumber) {
                this.data.telephone = item.PhoneNumber;
            } else {
                this.data.telephone = this.phoneTmp;
            }

            this.changeDetectorRef.detectChanges();
        }
    }

    selectPhoneCode(code: string, close = true): void {
        this.phoneCountryCode = code;
        const countries = [...this.countryCodesDefault];
        // move this country to the first place on the list
        this.countryCodes = [code, ...countries.filter(c => c !== code)];
        // save last manually selected country code
        this._previousPhoneCountryCode = code;

        if (close) {
            this.closePhoneSelect();
        }

        this.changeDetectorRef.detectChanges();
    }

    togglePhoneSelect(): void {
        this.toggleService.toggle(this.phoneToggleId);
    }

    closePhoneSelect(): void {
        this.toggleService.close(this.phoneToggleId);

        this.phoneQuery = '';
    }
}
